import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useCatalogApi from "hooks/api/private/useCatalogApi";
import useSnackbar from "hooks/useSnackbar";

import { CatalogListItem } from "types/api/catalog";

export type CatalogListHook = {
    navigateToNewCatalog: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    dataTableHook: DataTableHook<CatalogListItem>;
    alertConfirmHook: AlertConfirmHookType;
    staticFilterHook: StaticTableFilterHook;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
};

function useCatalogList(): CatalogListHook {
    const { getAllCatalog, deleteCatalog } = useCatalogApi();

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const navigate = useNavigate();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    graphical_order: {
                        key: "graphical_order",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "catalog-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.title = value;
                newForm.search.graphical_order = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            front_visible: {
                key: "front_visible",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            reserved_area_visible: {
                key: "reserved_area_visible",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            title: {
                key: "title",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "catalog-list-static",
        memoizedFieldsForStaticFilters,
    );

    const navigateToEditCatalog = React.useCallback(
        (row: CatalogListItem) => {
            navigate(makeUrl(":catalogId/modifica", { catalogId: row.id }));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Catalogo",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteCatalogButton = React.useCallback(
        (row: CatalogListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare il Catalogo?", () => {
                const [apiCall] = deleteCatalog({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Catalogo eliminato con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del catalogo"));
            });
        },
        [
            enqueueSuccessSnackbar,
            setTriggerReloadTable,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteCatalog,
        ],
    );

    const columns: Array<Column<CatalogListItem>> = React.useMemo(
        (): Array<Column<CatalogListItem>> => [
            {
                id: "catalogues.graphical_order",
                header: "Ordine",
                width: "85px",
                render: (row: CatalogListItem) => row.graphical_order,
                orderable: true,
            },
            {
                id: "catalogues_languages.title",
                header: "Titolo",
                render: (row: CatalogListItem) => row.title,
                orderable: true,
            },
            {
                id: "front_visible",
                header: "Area pubblica",
                width: "130px",
                render: (row: CatalogListItem) => (row.front_visible ? "Visibile" : "Non visibile"),
            },
            {
                id: "reserved_area_visible",
                header: "Area riservata",
                width: "130px",
                render: (row: CatalogListItem) =>
                    row.reserved_area_visible ? "Visibile" : "Non visibile",
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: CatalogListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditCatalog}
                        onDelete={deleteCatalogButton}
                    />
                ),
            },
        ],
        [navigateToEditCatalog, deleteCatalogButton],
    );

    const { filters: searchfilters } = filterSearchHook;
    const { filters: staticfilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchfilters, ...staticfilters],
        [searchfilters, staticfilters],
    );

    const extraCallParams = React.useMemo(
        () => ({
            sortAdvanced: JSON.stringify([
                { field: "catalogues.graphical_order", direction: "ASC" },
            ]),
        }),
        [],
    );

    const dataTableHook = useDataTableWithMemory<CatalogListItem>({
        apiCall: getAllCatalog,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        extraCallParams: extraCallParams,
        size: 20,
        key: "catalog-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewCatalog = React.useCallback(() => {
        navigate("/cataloghi/nuovo");
    }, [navigate]);

    return React.useMemo(
        (): CatalogListHook => ({
            navigateToNewCatalog,
            onSearchFilterChange,
            alertConfirmHook,
            staticFilterHook,
            filterSearchHook,
            dataTableHook,
        }),
        [
            navigateToNewCatalog,
            onSearchFilterChange,
            alertConfirmHook,
            staticFilterHook,
            filterSearchHook,
            dataTableHook,
        ],
    );
}

export default useCatalogList;
